import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SEOHead } from '../../components/seo/SEOHead';
import { FAQSchema, BreadcrumbSchema, ServiceSchema } from '../../components/seo/SchemaMarkup';
import { Breadcrumbs } from '../../components/seo/Breadcrumbs';
import { LatestArticles } from '../../components/blog/LatestArticles';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { MapPin, Clock, Wine, Music, Star, ArrowRight } from 'lucide-react';

export default function BarJamshedpur() {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "What is the best bar in Jamshedpur?",
      answer: "Scratch Unleashed at Bindal Mall, Sonari is widely regarded as Jamshedpur's best bar and nightclub. It combines a premium cocktail bar, bottle service lounge, and nightclub experience in one venue with professional bartenders and a world-class sound system."
    },
    {
      question: "What cocktails are available at Scratch Unleashed?",
      answer: "Scratch Unleashed offers an extensive cocktail menu including signature creations, classic cocktails (Mojito, Old Fashioned, Margarita), premium Scotch and whiskey selections, craft gin options, wine, and premium spirits. Our mixologists create custom drinks on request."
    },
    {
      question: "Is there a cover charge at Scratch Unleashed bar?",
      answer: "Cover charges vary by day and event. Regular weekend entry starts from ₹1500 per person. Some nights feature complimentary entry for couples or ladies. VIP table bookings include complimentary entry for your group. Check our events page for current pricing."
    },
    {
      question: "Can couples visit Scratch Unleashed?",
      answer: "Absolutely! Scratch Unleashed is very couple-friendly. We offer couple entry packages and special tables for twos. Our venue maintains a safe, sophisticated atmosphere that's perfect for date nights and anniversary celebrations."
    },
    {
      question: "What is the minimum spend at the bar?",
      answer: "There is no mandatory minimum spend at the main bar. VIP table bookings start from ₹20,000 minimum spend which includes premium bottle service. Walk-in guests can enjoy our full bar menu without table commitments."
    }
  ];

  const breadcrumbItems = [
    { name: 'Bar in Jamshedpur', url: null }
  ];

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <SEOHead
        title="Bar in Jamshedpur | Best Bars & Pubs - Scratch Unleashed"
        description="Looking for the best bar in Jamshedpur? Scratch Unleashed offers premium cocktails, whiskey selection, craft drinks & nightclub vibes. Couple-friendly. Bindal Mall, Sonari."
        keywords="bar in Jamshedpur, best bar Jamshedpur, pub in Jamshedpur, lounge bar Jamshedpur, cocktail bar Jamshedpur, couple entry Jamshedpur, bar and restaurant Jamshedpur, premium bar Jamshedpur, Bindal Mall bar"
        canonical="https://scratchjsr.com/bar-in-jamshedpur"
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        serviceName="Premium Bar & Cocktail Lounge"
        serviceDescription="Premium bar and cocktail lounge experience in Jamshedpur with signature cocktails, premium spirits, and world-class service."
        serviceUrl="https://scratchjsr.com/bar-in-jamshedpur"
      />
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://scratchjsr.com/' },
        { name: 'Bar in Jamshedpur', url: 'https://scratchjsr.com/bar-in-jamshedpur' }
      ]} />

      <div className="h-16"></div>

      <div className="max-w-[1120px] mx-auto px-4 sm:px-6 py-12">
        <Breadcrumbs items={breadcrumbItems} />

        <div className="mb-12">
          <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl mb-4">
            BAR IN JAMSHEDPUR
          </h1>
          <p className="text-xl text-[var(--text-dim)] mb-6">
            Premium Cocktails, Whiskey & Nightclub Vibes at Scratch Unleashed
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button className="btn-crimson-glow rounded-xl" onClick={() => navigate('/checkin')}>
              Reserve Your Table
              <ArrowRight size={16} className="ml-2" />
            </Button>
            <Button variant="outline" className="rounded-xl border-[var(--border-custom)]" onClick={() => navigate('/vip-table-booking-jamshedpur')}>
              VIP Table Booking
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-8">
            <section>
              <h2 className="font-bebas text-3xl mb-4">Best Bar in Jamshedpur</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  Searching for the <strong>best bar in Jamshedpur</strong>? Scratch Unleashed delivers a premium bar experience unlike anything else in the city. Located on the 3rd floor of Bindal Mall in Sonari, our venue combines a world-class cocktail bar with a nightclub atmosphere — the perfect evening destination whether you're coming for drinks or the full nightlife experience.
                </p>
                <p>
                  As Jamshedpur's most talked-about <strong>lounge bar</strong>, we've assembled an outstanding bar team. Our mixologists bring genuine craft to every drink — from perfectly balanced classics to creative signature cocktails that reflect the energy of our venue. Behind the bar you'll find an impressive selection of premium Scotch whiskies, Japanese whiskey, craft gins, premium vodkas, aged rums, and our carefully curated wine list.
                </p>
              </div>
            </section>

            <section>
              <h2 className="font-bebas text-3xl mb-4">Cocktail Bar & Premium Spirits</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  Our <strong>cocktail bar in Jamshedpur</strong> features over 40 drinks on the menu, from timeless classics to house specialties developed by our bar team. Signature cocktails change seasonally, ensuring repeat visits always bring something new. Popular favourites include our Smoky Negroni, Gold Rush (bourbon, honey, lemon), Dark & Stormy, and our signature Scratch Sour.
                </p>
                <p>
                  For whiskey lovers, our back bar holds over 30 single malts and blended Scotches, premium bourbons, and rare Japanese expressions. Whether you're a connoisseur exploring new bottles or someone who simply wants a quality dram, our staff are knowledgeable and happy to guide your selection.
                </p>
                <p>
                  Bottle service at our <strong>pub and bar in Jamshedpur</strong> comes with a dedicated host, ice service, mixers, and the best table location in the venue. It's the most popular option for groups celebrating birthdays, anniversaries, or just making a night memorable.
                </p>
              </div>
            </section>

            <section>
              <h2 className="font-bebas text-3xl mb-4">Couple-Friendly Bar in Jamshedpur</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  Scratch Unleashed is Jamshedpur's most <strong>couple-friendly bar and nightclub</strong>. We maintain a welcoming, sophisticated atmosphere designed for pairs as much as groups. Our seating includes intimate two-tops perfect for date nights, as well as larger lounge sections for groups.
                </p>
                <p>
                  Special couple packages include reserved seating, a welcome cocktail or mocktail on arrival, and the option to add personalised touches for anniversaries or special occasions. For <strong>couple entry in Jamshedpur</strong> nightlife, Scratch Unleashed remains the top choice — safe, welcoming, and consistently delivering memorable evenings.
                </p>
              </div>
            </section>

            <section className="bg-[var(--surface)] border border-[var(--border-custom)] rounded-2xl p-8">
              <h2 className="font-bebas text-3xl mb-4">Visit Jamshedpur's Best Bar Tonight</h2>
              <p className="text-[var(--text-dim)] mb-6">
                Reserve your table at Scratch Unleashed and experience Jamshedpur's finest bar. Fridays, Saturdays and Sundays, 6 PM until late.
              </p>
              <Button className="btn-crimson-glow rounded-xl" onClick={() => navigate('/checkin')}>
                Book Now
              </Button>
            </section>

            <section>
              <h2 className="font-bebas text-3xl mb-6">Frequently Asked Questions</h2>
              <Accordion type="single" collapsible className="space-y-3">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="border border-[var(--border-custom)] rounded-xl px-4 bg-[var(--surface)]">
                    <AccordionTrigger className="hover:no-underline text-left">
                      <span className="font-semibold">{faq.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-[var(--text-dim)] pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          </div>

          <div className="lg:col-span-1 space-y-6">
            <Card className="p-6 bg-[var(--surface)] border border-[var(--border-custom)] rounded-xl">
              <h3 className="font-bebas text-2xl mb-4">Quick Info</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Clock size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Opening Hours</p>
                    <p className="text-sm text-[var(--text-dim)]">Fri-Sun: 6 PM - Late</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Location</p>
                    <p className="text-sm text-[var(--text-dim)]">3rd Floor, Bindal Mall, Sonari, Jamshedpur</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Wine size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Bar Menu</p>
                    <p className="text-sm text-[var(--text-dim)]">40+ cocktails, 30+ whiskies, premium spirits</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Star size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Rating</p>
                    <p className="text-sm text-[var(--text-dim)]">4.6/5 — Jamshedpur's top-rated nightlife venue</p>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-0 overflow-hidden bg-[var(--surface)] border border-[var(--border-custom)] rounded-xl">
              <div className="aspect-square">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3666.826790556041!2d86.19787467526635!3d22.805879979362773!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39f5e3bc0f055ce3%3A0x94fae8cb5f18ece8!2sScratch%20Unleashed%20Bar%20%26%20Kitchen!5e0!3m2!1sen!2sin!4v1712345678901!5m2!1sen!2sin"
                  width="100%" height="100%" style={{ border: 0 }}
                  allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                  title="Scratch Unleashed Bar - Bindal Mall, Sonari, Jamshedpur"
                />
              </div>
            </Card>

            <LatestArticles maxPosts={3} />

            <Card className="p-6 bg-[var(--surface)] border border-[var(--border-custom)] rounded-xl">
              <h3 className="font-bebas text-2xl mb-4">Explore More</h3>
              <div className="space-y-3">
                <button onClick={() => navigate('/night-club-in-jamshedpur')} className="w-full text-left text-sm text-[var(--accent-gold)] hover:underline">
                  → Night Club in Jamshedpur
                </button>
                <button onClick={() => navigate('/ladies-night-jamshedpur')} className="w-full text-left text-sm text-[var(--accent-gold)] hover:underline">
                  → Ladies Night Jamshedpur
                </button>
                <button onClick={() => navigate('/vip-table-booking-jamshedpur')} className="w-full text-left text-sm text-[var(--accent-gold)] hover:underline">
                  → VIP Table Booking
                </button>
                <button onClick={() => navigate('/birthday-party-venue-jamshedpur')} className="w-full text-left text-sm text-[var(--accent-gold)] hover:underline">
                  → Birthday Party Venue
                </button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
